// fileIcons.js

/**
 * Mapping of file extensions to Font Awesome 5 icon classes
 * @type {Object.<string, string>}
 */
const fileIconMap = {
	// Documents
	pdf: "fas fa-file-pdf",
	doc: "fas fa-file-word",
	docx: "fas fa-file-word",
	txt: "fas fa-file-alt",
	rtf: "fas fa-file-alt",
	odt: "fas fa-file-alt",

	// Spreadsheets
	xls: "fas fa-file-excel",
	xlsx: "fas fa-file-excel",
	csv: "fas fa-file-csv",
	ods: "fas fa-file-excel",

	// Presentations
	ppt: "fas fa-file-powerpoint",
	pptx: "fas fa-file-powerpoint",
	odp: "fas fa-file-powerpoint",

	// Images
	jpg: "fas fa-file-image",
	jpeg: "fas fa-file-image",
	png: "fas fa-file-image",
	gif: "fas fa-file-image",
	bmp: "fas fa-file-image",
	svg: "fas fa-file-image",
	webp: "fas fa-file-image",

	// Audio
	mp3: "fas fa-file-audio",
	wav: "fas fa-file-audio",
	ogg: "fas fa-file-audio",
	m4a: "fas fa-file-audio",

	// Video
	mp4: "fas fa-file-video",
	avi: "fas fa-file-video",
	mov: "fas fa-file-video",
	wmv: "fas fa-file-video",
	mkv: "fas fa-file-video",

	// Archives
	zip: "fas fa-file-archive",
	rar: "fas fa-file-archive",
	"7z": "fas fa-file-archive",
	tar: "fas fa-file-archive",
	gz: "fas fa-file-archive",

	// Code
	html: "fas fa-file-code",
	css: "fas fa-file-code",
	js: "fas fa-file-code",
	json: "fas fa-file-code",
	xml: "fas fa-file-code",

	// Default
	default: "fas fa-file"
};

/**
 * Get the Font Awesome icon class for a given filename
 * @param {string} filename - The name of the file including extension
 * @returns {string} Font Awesome icon class
 */
export function getFileIcon(filename) {
	if (!filename) return fileIconMap.default;
	const extension = filename
		.split(".")
		.pop()
		.toLowerCase();
	return fileIconMap[extension] || fileIconMap.default;
}

/**
 * Check if a file is an image based on its extension
 * @param {string} filename - The name of the file including extension
 * @returns {boolean}
 */
export function isImage(filename) {
	if (!filename) return false;
	const extension = filename
		.split(".")
		.pop()
		.toLowerCase();
	const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];
	return imageExtensions.includes(extension);
}

/**
 * Check if a file is a video based on its extension
 * @param {string} filename - The name of the file including extension
 * @returns {boolean}
 */
export function isVideo(filename) {
	if (!filename) return false;
	const extension = filename
		.split(".")
		.pop()
		.toLowerCase();
	const videoExtensions = ["mp4", "avi", "mov", "wmv", "mkv"];
	return videoExtensions.includes(extension);
}

/**
 * Get a human-readable file type description
 * @param {string} filename - The name of the file including extension
 * @returns {string} Description of the file type
 */
export function getFileType(filename) {
	if (!filename) return "Unknown";
	const extension = filename
		.split(".")
		.pop()
		.toLowerCase();

	const typeMap = {
		pdf: "PDF Document",
		doc: "Word Document",
		docx: "Word Document",
		txt: "Text File",
		rtf: "Rich Text Document",
		xls: "Excel Spreadsheet",
		xlsx: "Excel Spreadsheet",
		csv: "CSV File",
		ppt: "PowerPoint Presentation",
		pptx: "PowerPoint Presentation",
		jpg: "Image",
		jpeg: "Image",
		png: "Image",
		gif: "Image",
		mp3: "Audio File",
		wav: "Audio File",
		mp4: "Video File",
		avi: "Video File",
		zip: "Archive",
		rar: "Archive",
		"7z": "Archive"
	};

	return typeMap[extension] || "File";
}

export default {
	getFileIcon,
	isImage,
	isVideo,
	getFileType,
	fileIconMap
};
